import {
	IssueType,
	PORTAL_ISSUE_MESSAGE_MAX_LENGTH,
	PORTAL_ISSUE_SUBJECT_MAX_LENGTH,
	PORTAL_ISSUE_TYPES,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { submitPortalIssue } from '../services/portalIssueService';

const INPUT_CLASSES =
	'w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default';

/**
 * The support-request form (PD-13705, extracted in PD-15002) — category /
 * subject / message with shared server-mirrored limits. Used by the
 * dashboard Submit-an-Issue widget and the Support page's New Support
 * Request modal, so the two entry points can never drift.
 *
 * Owns the field + submit state; the caller decides what happens after a
 * successful submission via onSuccess (the widget shows its reference
 * screen, the Support page navigates to the new request's detail).
 */
export function SupportRequestForm({
	onSuccess,
	submitLabel,
}: {
	onSuccess: (issueId: number) => void;
	/** Defaults to 'Send' (the widget's historical label). */
	submitLabel?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();

	const [type, setType] = useState<IssueType>(IssueType.QUESTION);
	const [subject, setSubject] = useState('');
	const [message, setMessage] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (token == null) {
		return null;
	}

	const handleSubmit = async (): Promise<void> => {
		const trimmedSubject = subject.trim();
		const trimmedMessage = message.trim();

		if (trimmedSubject.length === 0 || trimmedMessage.length === 0) {
			setError(t('Please fill in a subject and a message.'));
			return;
		}

		setSubmitting(true);
		setError(null);

		try {
			const result = await submitPortalIssue(token, {
				subject: trimmedSubject,
				type,
				message: trimmedMessage,
			});
			setSubject('');
			setMessage('');
			setType(IssueType.QUESTION);
			onSuccess(result.issueId);
		} catch {
			setError(t("We couldn't send your request right now. Please try again."));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form
			className="flex-1 flex flex-col gap-3"
			onSubmit={(e) => {
				e.preventDefault();
				void handleSubmit();
			}}
		>
			<label className="block">
				<span className="block text-xs font-medium text-brand-muted mb-1">
					{t('Category')}
				</span>
				<select
					value={type}
					onChange={(e) => {
						setType(e.target.value as IssueType);
					}}
					disabled={submitting}
					className={INPUT_CLASSES}
				>
					{PORTAL_ISSUE_TYPES.map((issueType) => (
						<option key={issueType} value={issueType}>
							{t(issueType)}
						</option>
					))}
				</select>
			</label>

			<label className="block">
				<span className="block text-xs font-medium text-brand-muted mb-1">
					{t('Subject')}
				</span>
				<input
					type="text"
					value={subject}
					onChange={(e) => {
						setSubject(e.target.value);
					}}
					maxLength={PORTAL_ISSUE_SUBJECT_MAX_LENGTH}
					placeholder={t('What is this about?')}
					disabled={submitting}
					className={INPUT_CLASSES}
				/>
			</label>

			<label className="block flex-1 flex flex-col">
				<span className="block text-xs font-medium text-brand-muted mb-1">
					{t('Message')}
				</span>
				<textarea
					value={message}
					onChange={(e) => {
						setMessage(e.target.value);
					}}
					maxLength={PORTAL_ISSUE_MESSAGE_MAX_LENGTH}
					placeholder={t('Tell us what you need help with...')}
					disabled={submitting}
					rows={4}
					className={`${INPUT_CLASSES} flex-1 resize-none min-h-[96px]`}
				/>
			</label>

			{error != null && <p className="text-xs text-danger">{error}</p>}

			<button
				type="submit"
				disabled={submitting}
				className="w-full px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50"
			>
				{submitting ? t('Sending...') : t(submitLabel ?? 'Send')}
			</button>
		</form>
	);
}
